import { ErrorAttr } from "@services/common/types";
import Swal from "sweetalert2";
import { renderToStaticMarkup } from "react-dom/server";
import { toast } from "react-hot-toast";

/**
 * Show backend's error message by default, and fallback to a custom error message.
 */
const errorMessage =
  (fallbackMessage: string) =>
  (error: ErrorAttr): string => {
    if (
      typeof error.response?.data.message === "string" &&
      error.response?.data.error
    ) {
      return error.response.data.message;
    }

    return fallbackMessage;
  };

/**
 * Handle mutations that need confirmation before sending a request.
 */
const createMutationHandler = (
  {
    areYouSureTitle,
    areYouSureDescription,
    confirmButtonLabel = "Confirm",
    cancelButtonLabel = "Cancel",
    component,
  }: {
    areYouSureTitle: string;
    areYouSureDescription?: string;
    confirmButtonLabel?: string;
    cancelButtonLabel?: string;
    component?: React.ReactElement;
  },
  mutation: () => Promise<unknown>,
  asyncMessages: {
    loading: string;
    success: string | ((data: unknown) => string);
    error: string;
  }
): void =>
  void Swal.fire({
    title: areYouSureTitle,
    text: areYouSureDescription,
    icon: "warning",
    html: component ? renderToStaticMarkup(component) : undefined,
    showCancelButton: true,
    confirmButtonText: confirmButtonLabel,
    cancelButtonText: cancelButtonLabel,
    reverseButtons: true,

    customClass: {
      popup: "px-6 md:max-w-[380px] md:pb-12",
      icon: "border-2 border-gray-200 rounded-full p-2 font-light mt-5 ",
      title: "text-sm font-light",
      actions: " block md:flex w-full justify-center",
      htmlContainer: "mx-0 md:mx-6",
      cancelButton: "w-full h-[25px] py-0 text-sm mx-0 md:w-[115px]",
      confirmButton: "w-full h-[25px] py-0 text-sm mx-0 md:w-[115px] md:ml-6",
      container: "z-[9999]",
    },
    hideClass: {
      icon: "swal2-icon-hide",
    },
  }).then((result) => {
    if (result.value) {
      const mutationPromise = mutation();

      void toast.promise(mutationPromise, {
        ...asyncMessages,
        error: errorMessage(asyncMessages.error),
      });
    }
  });

export { createMutationHandler, errorMessage };
